import { useCallback, useEffect, useRef, useState } from 'react';
import { isEmptyValue, PAYMENT_METHODS } from './bookingGate';
import type {
  BookEventHostData,
  BookEventPriceResult,
  BookEventUser,
  PaymentMethodOption,
} from './bookEvent.types';
import { getServiceID, getServiceType, gotOutOfCredits, maxGuests } from './bookEventRules';
import { buildBranchPaymentOptions, loadPaymentMethods } from './paymentMethods';
import type { PriceBreakdownData } from './price-breakdown/PriceBreakdown';

function needsCombinedPayment(response: BookEventPriceResult, guests: number): boolean {
  const credits = response.credits ?? 0;
  return [
    credits > 0,
    (response.price ?? 0) > 0,
    gotOutOfCredits(guests, credits),
  ].every(Boolean);
}

type PricingArgs = {
  data: BookEventHostData;
  user: BookEventUser | null;
  setUser: (user: BookEventUser | null) => void;
};

function normalizePrice(response: BookEventPriceResult | void) {
  return {
    credits: response?.credits ?? 0,
    price: response?.price ?? null,
    currency: response?.currency ?? 'EUR',
  };
}

function completePriceRequest(
  pendingPriceRequests: { current: number },
  stopLoading: () => void
) {
  pendingPriceRequests.current = Math.max(0, pendingPriceRequests.current - 1);
  if (pendingPriceRequests.current === 0) {
    stopLoading();
  }
}

function usePriceQuote(data: BookEventHostData, guests: number) {
  const [priceToPay, setPriceToPay] = useState<number | null>(null);
  const [creditsToPay, setCreditsToPay] = useState(0);
  const [currency, setCurrency] = useState('EUR');
  const [isPriceLoading, setIsPriceLoading] = useState(false);
  const pendingPriceRequests = useRef(0);
  const priceGeneration = useRef(0);

  const requestPrice = useCallback(
    async (options?: { silent?: boolean }) => {
      const generation = ++priceGeneration.current;
      pendingPriceRequests.current += 1;
      setIsPriceLoading(true);
      try {
        const response = await data.getEventPrice(data.event, guests, options);
        if (generation !== priceGeneration.current) {
          return undefined;
        }
        const price = normalizePrice(response);
        setCreditsToPay(price.credits);
        setPriceToPay(price.price);
        setCurrency(price.currency);
        return price;
      } catch (priceError) {
        console.log('Error resolving price => ', priceError);
        throw priceError;
      } finally {
        completePriceRequest(pendingPriceRequests, () => setIsPriceLoading(false));
      }
    },
    [data, guests]
  );

  return {
    priceToPay,
    creditsToPay,
    currency,
    isPriceLoading,
    requestPrice,
  };
}

function usePaymentOptions(data: BookEventHostData, user: BookEventUser | null) {
  const [paymentOptions, setPaymentOptions] = useState<PaymentMethodOption[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<PaymentMethodOption>();
  const [paymentDropdownOpen, setPaymentDropdownOpen] = useState(false);
  const applyPaymentMethods = useCallback(
    (nextPrice: number | null, nextCredits: number, paymentUser = user) => {
      const loaded = loadPaymentMethods({
        userHasCredits: nextCredits > 0,
        priceToPay: nextPrice,
        branch: data.branch,
        user: paymentUser,
        translate: data.translate,
      });
      setPaymentOptions(loaded.options);
      setSelectedPayment(loaded.selected);
    },
    [data.branch, data.translate, user]
  );

  return {
    paymentOptions,
    setPaymentOptions,
    selectedPayment,
    setSelectedPayment,
    paymentDropdownOpen,
    setPaymentDropdownOpen,
    applyPaymentMethods,
  };
}

function useInitializePrice(
  requestPrice: ReturnType<typeof usePriceQuote>['requestPrice'],
  applyPaymentMethods: ReturnType<typeof usePaymentOptions>['applyPaymentMethods']
) {
  return useCallback(() => {
    void requestPrice().then((response) => {
      if (!response) {
        return;
      }
      applyPaymentMethods(response.price, response.credits);
    });
  }, [applyPaymentMethods, requestPrice]);
}

function useUserPriceRefresh(
  user: BookEventUser | null,
  requestPrice: ReturnType<typeof usePriceQuote>['requestPrice'],
  applyPaymentMethods: ReturnType<typeof usePaymentOptions>['applyPaymentMethods']
) {
  const skipNextUserPrice = useRef(true);
  useEffect(() => {
    if (skipNextUserPrice.current) {
      skipNextUserPrice.current = false;
      return;
    }
    void requestPrice().then((response) => {
      if (!response) {
        return;
      }
      applyPaymentMethods(response.price, response.credits);
    });
  }, [user]);
}

function updateCurrentGeneration(
  generationRef: { current: number },
  generation: number,
  update: () => void
) {
  if (generation === generationRef.current) {
    update();
  }
}

async function loadPriceBreakdown(
  data: BookEventHostData,
  priceToPay: number,
  currency: string
): Promise<PriceBreakdownData> {
  try {
    const built = await data.getPriceBreakdown(
      [
        {
          name: data.event.name || '',
          price: priceToPay,
          type: getServiceType(data.event),
          service_id: getServiceID(data.event),
        },
      ],
      currency
    );
    return {
      isLoading: false,
      error: null,
      rows: built?.rows || [],
      footnotes: built?.footnotes || [],
    };
  } catch {
    return {
      isLoading: false,
      error: data.translate('WEB_FAILED_TO_FETCH_TAX_BREAKDOWN'),
      rows: [],
      footnotes: [],
    };
  }
}

function usePriceBreakdown(
  data: BookEventHostData,
  priceToPay: number | null,
  currency: string
) {
  const [isPriceBreakdownLoading, setIsPriceBreakdownLoading] = useState(false);
  const [priceBreakdown, setPriceBreakdown] = useState<PriceBreakdownData | null>(null);
  const priceBreakdownGeneration = useRef(0);
  const refreshPriceBreakdown = useCallback(async () => {
    const generation = ++priceBreakdownGeneration.current;
    const price = priceToPay ?? 0;
    if (price <= 0) {
      setPriceBreakdown(null);
      setIsPriceBreakdownLoading(false);
      return;
    }
    setIsPriceBreakdownLoading(true);
    try {
      const breakdown = await loadPriceBreakdown(data, price, currency);
      updateCurrentGeneration(priceBreakdownGeneration, generation, () => {
        setPriceBreakdown(breakdown);
      });
    } finally {
      updateCurrentGeneration(priceBreakdownGeneration, generation, () => {
        setIsPriceBreakdownLoading(false);
      });
    }
  }, [currency, data, priceToPay]);

  useEffect(() => {
    void refreshPriceBreakdown();
  }, [refreshPriceBreakdown]);

  return { isPriceBreakdownLoading, priceBreakdown };
}

function useGuestPriceRefresh(args: {
  guests: number;
  data: BookEventHostData;
  user: BookEventUser | null;
  requestPrice: ReturnType<typeof usePriceQuote>['requestPrice'];
  applyPaymentMethods: ReturnType<typeof usePaymentOptions>['applyPaymentMethods'];
  setPaymentOptions: ReturnType<typeof usePaymentOptions>['setPaymentOptions'];
  setSelectedPayment: ReturnType<typeof usePaymentOptions>['setSelectedPayment'];
}) {
  const skipNextGuestPrice = useRef(true);
  const previousGuests = useRef(args.guests);
  useEffect(() => {
    const guestsIncreased = args.guests > previousGuests.current;
    previousGuests.current = args.guests;
    if (skipNextGuestPrice.current) {
      skipNextGuestPrice.current = false;
      return;
    }
    void args.requestPrice({ silent: true }).then((response) => {
      if (!response) {
        return;
      }
      const creditsStillCoverParty =
        response.credits > 0 && !gotOutOfCredits(args.guests, response.credits);
      if (guestsIncreased || creditsStillCoverParty) {
        args.applyPaymentMethods(response.price, response.credits);
      }
      if (guestsIncreased && needsCombinedPayment(response, args.guests)) {
        const combined = buildBranchPaymentOptions(
          args.data.branch,
          args.user,
          args.data.translate,
          'combined'
        );
        args.setPaymentOptions(combined.options);
        args.setSelectedPayment(combined.selected);
      }
    });
  }, [args.guests]);
}

function useGuestControls(data: BookEventHostData) {
  const [guests, setGuests] = useState(0);
  const decreaseGuests = useCallback(() => {
    if (guests > 0) {
      setGuests(guests - 1);
    }
  }, [guests]);

  const increaseGuests = useCallback(() => {
    if (guests < maxGuests(data.event, data.branch)) {
      setGuests(guests + 1);
    }
  }, [data.branch, data.event, guests]);

  return { guests, decreaseGuests, increaseGuests };
}

function useCollectPaymentCard(args: {
  data: BookEventHostData;
  setUser: PricingArgs['setUser'];
  priceToPay: number | null;
  creditsToPay: number;
  applyPaymentMethods: ReturnType<typeof usePaymentOptions>['applyPaymentMethods'];
}) {
  const collectPaymentCard = useCallback(async () => {
    const refreshedUser = await args.data.showPaymentCollector();
    if (!refreshedUser) {
      return;
    }
    args.setUser(refreshedUser);
    args.applyPaymentMethods(args.priceToPay, args.creditsToPay, refreshedUser);
  }, [args]);
  return collectPaymentCard;
}

function useSetPaymentMethod(args: {
  user: BookEventUser | null;
  priceToPay: number | null;
  collectPaymentCard: () => Promise<void>;
  setSelectedPayment: ReturnType<typeof usePaymentOptions>['setSelectedPayment'];
  setPaymentDropdownOpen: ReturnType<typeof usePaymentOptions>['setPaymentDropdownOpen'];
}) {
  const setPaymentMethod = useCallback(
    (option: PaymentMethodOption) => {
      args.setSelectedPayment(option);
      args.setPaymentDropdownOpen(false);
      if (!args.priceToPay || !isEmptyValue(args.user?.card)) {
        return;
      }
      if (option.key.toLowerCase() !== PAYMENT_METHODS.CARD) {
        return;
      }
      void args.collectPaymentCard();
    },
    [args]
  );
  return setPaymentMethod;
}

export function useBookEventPricing(args: PricingArgs) {
  const guestControls = useGuestControls(args.data);
  const quote = usePriceQuote(args.data, guestControls.guests);
  const payment = usePaymentOptions(args.data, args.user);
  const breakdown = usePriceBreakdown(args.data, quote.priceToPay, quote.currency);
  const initializePrice = useInitializePrice(quote.requestPrice, payment.applyPaymentMethods);
  useUserPriceRefresh(args.user, quote.requestPrice, payment.applyPaymentMethods);
  useGuestPriceRefresh({
    guests: guestControls.guests,
    data: args.data,
    user: args.user,
    requestPrice: quote.requestPrice,
    applyPaymentMethods: payment.applyPaymentMethods,
    setPaymentOptions: payment.setPaymentOptions,
    setSelectedPayment: payment.setSelectedPayment,
  });
  const collectPaymentCard = useCollectPaymentCard({
    data: args.data,
    setUser: args.setUser,
    priceToPay: quote.priceToPay,
    creditsToPay: quote.creditsToPay,
    applyPaymentMethods: payment.applyPaymentMethods,
  });
  const setPaymentMethod = useSetPaymentMethod({
    user: args.user,
    priceToPay: quote.priceToPay,
    collectPaymentCard,
    setSelectedPayment: payment.setSelectedPayment,
    setPaymentDropdownOpen: payment.setPaymentDropdownOpen,
  });

  return {
    ...guestControls,
    ...quote,
    ...payment,
    ...breakdown,
    initializePrice,
    collectPaymentCard,
    setPaymentMethod,
  };
}
